// A MINOR WITH A PARENT PRESENT — PIERCING (TW-0128) AND TATTOO (TW-0150).
//
// For a piercing, 45 of the 51 state records allow it on a parent's written consent. For a tattoo,
// 33 do and 18 make it a crime whatever the parent signs. THE TWO ARE DIFFERENT RULES AND ARE READ
// SEPARATELY: Oregon and Washington both pierce a minor and neither will tattoo one, so a single
// "minors" switch would have produced a signed parental consent for a felony in two states. The
// shop has one setting per service for the same reason — piercing a 16-year-old's earlobe and
// tattooing them are not one decision, and most studios that do the first refuse the second.
//
// Phillip Dawson asked for the piercing half on the ad on 2026-09-08 and Chris answered that
// evening. Jose Velazquez of A Toda Madre Tattoos in Reno asked for the tattoo half on his first
// morning as a customer, 2026-09-10, and was right about Nevada.
//
// THIS IS THE FORM'S HALF OF ONE RULE, AND THE API HOLDS THE OTHER (state_forms.py
// `minor_refusal`). Same three gates, same codes, same order, both reading the SAME
// generated contract — `state-form-requirements.json`, whose four copies are byte-identical and
// tested for drift (since TW-0505 the page reads the shop's state's own slice of it, fetched by
// stateRules.ts, and the API its whole copy). The API is the one that decides: a browser can post
// anything, so submit_waiver runs the rule again against the shop's own row and refuses there too. What lives here is the
// screen — a minor whose piercing IS allowed must not be shown a wall, and one whose piercing is
// barred must be told which rule bars it, before they have filled anything in.
//
// The conformance table `minor_cases` rides IN the contract, so this implementation and the
// Python one are checked against the same cases in two languages (minors.test.ts,
// test_minors.py). A case that changes answer because a state record changed turns both red.
// TW-0505 — the live tables (stateRules.ts RULES), filled in place when the shop's state's file lands
import { RULES as contract, ruleState } from "./stateRules";
import { dayOf } from "./day";
import { STATE_NAMES } from "./stateForms";
import { PIERCING_LOCATIONS, locationLabel } from "./service";
import type { Lang, Strings } from "./i18n";

/** TW-0153 — one jurisdiction inside a state whose rule is not the state's.
 *
 *  `match` is EXACTLY what Google Places resolves for an address there and `kind` says which
 *  component it came from — measured 2026-09-10 against real calls, and the whole safety of this
 *  feature. An independent city (Baltimore City, St. Louis City, Carson City, the Virginia cities)
 *  belongs to no county and carries NO administrative_area_level_2 at all; everywhere else carries
 *  one. So Baltimore City ("Baltimore", from the locality) and Baltimore County ("Baltimore County",
 *  from administrative_area_level_2) are different KINDS, never two strings to be told apart by
 *  trimming a suffix — and Baltimore County allows what Baltimore City bans. */
export type CountyRule = {
  /** What a person reads: "Baltimore City", "Clark County". */
  label: string;
  /** What Google returns, matched exactly (case and surrounding space aside). */
  match: string;
  /** "county" — administrative_area_level_2 · "city" — a locality that carries none (an INDEPENDENT city) ·
   *  "locality" (TW-0227) — a city INSIDE a county, matched on the shop's city (the Places locality) AND its county,
   *  which is `within`: Hammond and East Chicago, both in Lake County, Indiana. */
  kind: "county" | "city" | "locality";
  /** TW-0227 — the county a `locality` sits in, as Places writes administrative_area_level_2 ("Lake County"). On a
   *  locality only. */
  within?: string;
  /** No minor's tattoo here at all, whatever the state says and whatever a parent signs. */
  bans: boolean;
  /** A floor this jurisdiction sets where the state has none (Clark 14, Philadelphia 16). */
  floorAge: number | null;
  /** What the SHOP has to confirm on top of the state's list, before the switch turns on. */
  requires: { id: string; quote: string }[];
  /** The jurisdiction's rule in the state record's own words. */
  quote: string;
  /** TW-0227 — a piercing county's own bars under 18, as the picker's values (Vigo County: Genital, Nipple). */
  restrictedPlacements?: string[];
  /** TW-0270 slice 5 — the papers this county's or city's own rule keeps with a minor's record (St. Louis County's notarized
   *  consent on a department form, Natrona County's), photographed at signing; absent where it names none. */
  papers?: Paper[];
  /** The department that wrote it, and its page — from the state record's `local[]`. */
  authority: string | null;
  authorityUrl: string | null;
};

/** One thing a state wants ON TOP of a parent's signature (TW-0153), in the rule's own words. `from` / `until`
 *  (TW-0226, 2026-09-25) are the product-zone days it is in force between, inclusive, when the rule starts or ends
 *  on a day — North Dakota's minor's own ID from 2026-10-01 — and absent otherwise: see `requirementInForce`. */
export type Requirement = { id: string; quote: string; from?: string | null; until?: string | null };

/** TW-0267 — A STATEMENT THE SIGNER TICKS, IN THE RULE'S OWN EXACT WORDS. Robert, owner of 2 Ton Gallery in Kingsport,
 *  Tennessee, 2026-10-01: "I noticed on the minor waiver it doesn't have the required state paragraph that needs to be in
 *  for the parent to check." Tennessee's body piercing rule has the parent, legal guardian or legal custodian sign a form
 *  that "Requires signing of a statement with the exact wording as follows" (Tenn. Comp. R. & Regs. 1200-23-06-.03(3)(c)).
 *  `text` is those words, from the state's record, read at the source; `who` is who ticks it ("guardian"); `cite` and
 *  `citeUrl` are where they were read. English whatever language the form is read in: the rule sets the words.
 *  `from` / `until` as on a requirement (TW-0226). */
export type Attestation = {
  id: string;
  quote: string;
  text: string;
  /** TW-0526 (M01) — a statement in our words carrying the rule's (the parent's word that they are present): its Spanish, and
   *  `exact: false`. Absent on a rule's exact words (TW-0267), which stay in English as the rule wrote them. */
  text_es?: string;
  exact?: boolean;
  /** TW-0526, the client-form side (2026-10-29) — the rule's own words with a Spanish rendering of ours beside them (Illinois's
   *  provision for a piercing in the mouth, which the rule sets "in substantially the following form"): `text` is the rule's,
   *  word for word, and `text_es` the Spanish a parent reading Spanish ticks. */
  translated?: boolean;
  /** TW-0526, the client-form side — the posted placements a statement is kept for (the picker's five in or through the mouth),
   *  asked only when the signing's spot is one of them; absent means every signing of the rule's service. */
  placements?: string[] | null;
  cite: string;
  citeUrl: string;
  who: string;
  from?: string | null;
  until?: string | null;
};

/** TW-0270 slice 5 (2026-10-16) — A PAPER THE STATE'S RULE KEEPS WITH A MINOR'S RECORD, photographed at signing (the all-states
 *  audit's M10): a notarized consent, a department's consent form, an affidavit, the certified copy of an emancipation decree.
 *  `name` is the rule's own words for the paper and `name_es` the Spanish the form shows instead (awaiting a native read);
 *  `quote` and `cite` are the rule's sentence that keeps it and where it was read. `route` is who is asked: "guardian", a parent
 *  or guardian signing for a minor, or "emancipated", a minor signing for themselves where the rule treats one as an adult.
 *  `place` is the county's or city's label where the paper is that jurisdiction's, absent on the state's own. The API reads the
 *  same lists the same way (state_forms.papers_for, papers_asked) and refuses a signing missing one (MINOR_PAPER_MISSING). */
export type Paper = {
  id: string;
  quote: string;
  name: string;
  name_es: string;
  cite: string;
  citeUrl?: string;
  route: "guardian" | "emancipated";
  place?: string | null;
  from?: string | null;
  until?: string | null;
  /** 2026-11-03 — the shop's box this paper waits on (`state_form`, New York's and Wisconsin's state form): asked only once the
   *  shop has ticked it (papersAsked). Absent, the paper is asked whatever the boxes say (Arkansas's). */
  onceTicked?: string;
};

/** The boxes a shop has ticked for one minors switch (`public_data.pierce_minors_ack` or `tattoo_minors_ack`), each a literal
 *  true: what a paper that waits on a box is read against (papersAsked). */
export type TickedBoxes = Record<string, boolean>;

/** TW-0226 — is this requirement in force on `day` (the product's day, day.ts; today's when omitted)? One not yet
 *  in force is neither asked of the client nor refused, so North Dakota's form asks for the minor's own ID from
 *  October 1, 2026 and not a day early — the switch throws itself, and nobody promotes on the day. */
export function requirementInForce(r: Requirement, day: string = dayOf()): boolean {
  return (!r.from || r.from <= day) && (!r.until || day <= r.until);
}

/** The state's requirements in force on `day`. */
function requiresOn(rule: MinorsRule | undefined, day: string): Requirement[] {
  return (rule?.requires ?? []).filter((r) => requirementInForce(r, day));
}

export type MinorsRule = {
  /** The one field to branch on, and it is already narrowed by the generator: a state whose rule is
   *  unclear, or that was read with low confidence, is `false` here. `no_state_rule` is NOT unclear
   *  and is `true`: the state licenses tattooing and sets no age for a piercing, so nothing bars it
   *  (HI, IA, VT, WA — read at the statutes; DC and MS are the real bans). */
  allowed: boolean;
  parentalConsent: string | null;
  confidence: string | null;
  /** What the parent is consenting to, in the rule's own words. Printed on the signed waiver. */
  conditions: string | null;
  /** Below this age there is no route at all, parent or not (AR/WI/KY 16, ID 14), but for the spot the floor's own words
   *  except, where they do (`floorEarlobeException`, TW-0595: Arkansas's and Kentucky's earlobe). */
  floorAge: number | null;
  /** TW-0595 — THE FLOOR'S OWN EXCEPTION FOR THE EARLOBE, in the rule's words: Arkansas's "except: ... (B) When piercing the
   *  earlobe." (17 CAR § 54-306(g)(4)), Kentucky's "with the exception of piercing the lobe of the ear." (902 KAR 45:070
   *  Section 7(2)). PIERCING ONLY, and only where the record carries one. On the Earlobe spot the floor above is lifted and
   *  nothing else is: no question is asked (neither names a tool), and the rule's requirements, papers and statements stand,
   *  where `earExemption` takes the whole rule off. Read by floorEarlobeExceptionApplies. */
  floorEarlobeException?: FloorEarlobeException;
  /** TW-0153 — what this state wants ON TOP of a parent's signature, each with the rule's own words.
   *  Most are things only a human at a counter can do and the SHOP confirms them once in Settings;
   *  the ones marked `blocks_form` in the vocabulary are asked here, at signing. The tattoo rule's
   *  since TW-0153, the piercing rule's own since TW-0224 (2026-09-25), each quoted from its own
   *  conditions; `both_ids` and `guardian_proof` are what the form itself asks for (a second photo). */
  requires?: Requirement[];
  /** TW-0267 — the statements this rule has a signer tick ON THE FORM, in its own exact words (Tennessee's parent's
   *  certification for a minor's piercing). Never a shop's prerequisite: the generator lifts them out of `requires`.
   *  Absent where the rule has none. Tennessee's piercing rule carried the first (TW-0267); since TW-0270 M01 (2026-10-01)
   *  Kentucky's and Alabama's for either service, Vermont's tattoo rule and Illinois's piercing rule carry one each. */
  attestations?: Attestation[];
  /** TW-0270 slice 5 — the papers this rule keeps with a minor's record, each photographed at signing on its route. Never a shop's
   *  prerequisite: the generator lifts them out of `requires`, the attestations' way. Absent where the rule names none. */
  papers?: Paper[];
  /** TW-0153 — the counties and independent cities inside this state whose rule differs from it, and since TW-0227
   *  the cities inside a county: a flat ban in Baltimore City, floors in Clark County, Philadelphia and Vigo County,
   *  extra prerequisites elsewhere, both parents in Hammond, East Chicago and Delaware County. EACH RULE CARRIES ITS
   *  OWN, quoted from its own conditions: the piercing rule's list is not the tattoo rule's (Vigo's floor is 14 for a
   *  piercing and 16 for a tattoo), and a reader never takes one for the other. */
  counties?: CountyRule[];
  /** The prose, which can name things that are not piercing sites at all — branding, scarification,
   *  tongue bifurcation, neck rings — so a shop still has to read it. PIERCING ONLY: a tattoo rule
   *  that allows a minor at all allows it anywhere, and narrows in `conditions` instead. */
  restricted?: string | null;
  /** The same bars as VALUES from the form's own picker (service.ts PIERCING_LOCATIONS), which is
   *  what code can act on. Generated from the prose; the generator refuses a value the picker does
   *  not offer, because a bar the form could never apply is one that only looks enforced. */
  restrictedPlacements?: string[];
  /** TW-0222 — the product-zone day that bar starts (North Dakota's, 2026-10-01): it bars nothing before
   *  it. Absent means the bar has always been the rule. */
  restrictedFrom?: string | null;
  /** The record's own boolean: the ear is treated differently here in SOME way (earlobeNote says how). What the
   *  refusal's generic ear note reads. What the form ACTS on is `earExemption` below. */
  earlobeExempt?: boolean;
  /** TW-0225 — how far this state writes the ear out of its minors rule, generated from the record's `earExemption`
   *  (scripts/state-forms.py ear_exemption_from). PIERCING ONLY; a tattoo rule carries none. */
  earExemption?: EarExemption;
  cite: string | null;
  citeUrl: string | null;
};

/** TW-0225 — WHERE THE STATE WRITES THE EAR OUT OF ITS MINORS RULE. `scope` "ear" is the whole ear, cartilage included
 *  (Michigan: "the perforation of human tissue other than an ear"); "perimeter" the earlobe and the ear's outer rim, the
 *  cartilage inside the rim inside the rule (Mississippi: "other than the outer perimeter or lobe of the ear", 2026-10-31);
 *  "lobe" the earlobe alone, cartilage inside the rule; "none" no carve-out a shop's minor ear piercing can stand on.
 *  `method` "stud_gun" when the carve-out holds only for a single-use stud-and-clasp gun, so a needle through a lobe is
 *  body piercing there; "any" when it does not care; null with "none". `quote` is the record's own earlobeNote words it
 *  stands on. */
export type EarExemption = {
  scope: "ear" | "perimeter" | "lobe" | "none";
  method: "any" | "stud_gun" | null;
  quote: string | null;
};

/** TW-0595 — the floor's own exception for the earlobe: the rule's words for it, and where they were read
 *  (scripts/state-forms.py floor_earlobe_exception_from). */
export type FloorEarlobeException = { quote: string; cite: string; citeUrl: string };

const PIERCING_MINORS = contract.piercing_minors as unknown as Record<string, MinorsRule>;
const TATTOO_MINORS = contract.tattoo_minors as unknown as Record<string, MinorsRule>;

/** TW-0225 — the placements from the form's own picker (service.ts PIERCING_LOCATIONS) that each carve-out reaches: the
 *  contract's `ear_exemption_reach`, which the generator checks against the picker. A lobe carve-out never reaches ear
 *  cartilage; a perimeter one reaches it because the outer rim is filed there, and its question keeps the tragus and the
 *  conch inside the rule; "Somewhere else", which could be anywhere, is reached by none. */
const EAR_REACH = ((contract as unknown as { ear_exemption_reach?: Record<string, string[]> }).ear_exemption_reach ??
  {}) as Record<string, string[]>;

const NO_EAR_EXEMPTION: EarExemption = { scope: "none", method: null, quote: null };

/** TW-0225 — this rule's ear carve-out, or "none": for a tattoo rule (which carries none), a state the contract does not
 *  carry, and any shape this bundle does not know, which is the asking direction. */
export function earExemptionOf(rule: MinorsRule | undefined): EarExemption {
  const e = rule?.earExemption;
  if (!e || (e.scope !== "ear" && e.scope !== "perimeter" && e.scope !== "lobe")) return NO_EAR_EXEMPTION;
  if (e.method !== "any" && e.method !== "stud_gun") return NO_EAR_EXEMPTION;
  return e;
}

/** TW-0225 — is the ear question this signing's to ask? The rule writes some of the ear out of itself AND the spot the
 *  client picked is one that carve-out reaches. A navel is never an ear piercing and ear cartilage is never one a lobe
 *  carve-out reaches, so for those nothing is asked and the rule applies as it always has. The same answer as the API's
 *  `state_forms.asks_ear_piercing`, from the same contract. */
export function asksEarPiercing(rule: MinorsRule | undefined, placement: string | null | undefined): boolean {
  const e = earExemptionOf(rule);
  if (e.scope === "none") return false;
  return (EAR_REACH[e.scope] ?? []).includes(`${placement ?? ""}`.trim());
}

/** TW-0225 — does the carve-out apply to THIS signing: the question is one to ask for this spot, and the answer was yes.
 *  Only a literal true is a yes; a question nobody answered never reads as one. */
export function earExemptionApplies(
  rule: MinorsRule | undefined,
  placement: string | null | undefined,
  earPiercing: boolean | null | undefined
): boolean {
  return earPiercing === true && asksEarPiercing(rule, placement);
}

/** TW-0595 — this rule's floor's own exception for the earlobe, or null: for a tattoo rule (which carries none), a state the
 *  contract does not carry, a state whose floor names none, and any shape this bundle does not know, which keeps the floor. */
export function floorEarlobeExceptionOf(rule: MinorsRule | undefined): FloorEarlobeException | null {
  const f = rule?.floorEarlobeException;
  return f && typeof f.quote === "string" && f.quote.trim() ? f : null;
}

/** TW-0595 — does the floor's own exception reach THIS spot: the rule carries one and the spot picked is one the lobe's reach
 *  holds (the contract's `ear_exemption_reach.lobe`: the Earlobe, never the cartilage, never "Somewhere else"). Nothing is
 *  asked: neither Arkansas's words nor Kentucky's name a tool, so the spot alone decides. A yes here lifts the floor and
 *  nothing else; the rule's own asks (ruleAsksFor, papersAsked, the statements) are untouched by it. The same answer as the
 *  API's `state_forms.floor_earlobe_exception_applies`, from the same contract. */
export function floorEarlobeExceptionApplies(rule: MinorsRule | undefined, placement: string | null | undefined): boolean {
  if (!floorEarlobeExceptionOf(rule)) return false;
  return (EAR_REACH.lobe ?? []).includes(`${placement ?? ""}`.trim());
}

/** Our picker offers "Somewhere else", and in a state that bars specific sites we cannot tell
 *  whether that free choice IS one of them. Mirrors state_forms.UNVERIFIABLE_PLACEMENT. */
export const UNVERIFIABLE_PLACEMENT = "Somewhere else";

/** A client under this age on the day of signing is a minor. Since TW-0503 (2026-10-29) the age is read off the birth date
 *  (birthDate.ts), never typed, and the page reads `age < MINOR_AGE` (Form.tsx): 0 is an age, a baby's ear piercing. */
export const MINOR_AGE = 18;

/** What this state says about a minor's PIERCING, or undefined for no state and for a state the
 *  contract does not carry. */
export function piercingMinorsRule(state: string | null | undefined): MinorsRule | undefined {
  const abbr = `${state ?? ""}`.trim().toUpperCase();
  return abbr ? PIERCING_MINORS[abbr] : undefined;
}

/** The same, for a minor's TATTOO. A separate table because it is a separate law. */
export function tattooMinorsRule(state: string | null | undefined): MinorsRule | undefined {
  const abbr = `${state ?? ""}`.trim().toUpperCase();
  return abbr ? TATTOO_MINORS[abbr] : undefined;
}

/** 2026-10-08 — THE STUD-GUN QUESTION IS THE SHOP'S OWN SWITCH, OFF UNTIL THE OWNER TURNS IT ON. Robert Jarrett of 2 Ton
 *  Gallery (Kingsport, Tennessee), by Instagram DM, a week after asking for the state's parent certification: "Our
 *  customers are getting confused about this part of the minor piercing form. Can you take it out completely. We only
 *  use a piercing needle and never use a gun." Seventeen states write an ear out of their minors rule ONLY when it is
 *  pierced with a single-use stud-and-clasp gun (`earExemption.method === "stud_gun"`), and the question the form asked
 *  under the ear spots there, "…done with a single-use stud-and-clasp gun?", means nothing at a shop that pierces with
 *  needles: the carve-out can never apply, and the question reads as noise or as a trap. So the carve-out exists for a
 *  shop only while `public_data.ask_stud_gun` is a literal true (Settings → Who can sign → Ear piercing with a stud
 *  gun); for every other shop the rule reads as if the state had no carve-out, and a minor's ear piercing follows the
 *  state's whole rule, the stricter direction. A state whose carve-out needs no gun (Michigan's "other than an ear",
 *  Texas's lobe: `method: "any"`) is untouched by the switch: its one question is about the spot, not a tool.
 *
 *  `shop` UNDEFINED is the rule as the contract writes it, which the exam reads (minors.test.ts runs the same cases the
 *  API does, and neither side carries a shop there) and which no signing is judged by; a shop object, null included, is
 *  a real shop and is read for the literal true. The API reads the same switch the same way (state_forms.rule_for_shop). */
export type StudGunSwitch = { ask_stud_gun?: boolean } | null;

export function ruleForShop(rule: MinorsRule | undefined, shop: StudGunSwitch | undefined): MinorsRule | undefined {
  if (!rule || shop === undefined) return rule;
  if (rule.earExemption?.method !== "stud_gun" || shop?.ask_stud_gun === true) return rule;
  const { earExemption: _notThisShops, ...rest } = rule;
  return rest;
}

/** The rule for what this client is here for today. Undefined when the service is neither of the
 *  two we know — including "" , which is what it is before they have picked. `shop` is the shop's
 *  row as the form holds it (its `public_data`), for the stud-gun switch above; omit it for the rule
 *  as the contract writes it. */
export function minorsRuleFor(
  service: string | null | undefined,
  state: string | null | undefined,
  shop?: StudGunSwitch
): MinorsRule | undefined {
  const s = `${service ?? ""}`.trim().toLowerCase();
  if (s === "piercing") return ruleForShop(piercingMinorsRule(state), shop);
  if (s === "tattoo") return tattooMinorsRule(state);
  return undefined;
}

export type RefusalCode =
  | "no_service"
  | "shop_off"
  | "no_state"
  | "state_no"
  | "county_no"
  | "under_floor"
  | "coverup_only"
  | "placement_no"
  | "placement_unverifiable";

export type Refusal = {
  code: RefusalCode;
  /** coverup_only: did they answer NO, or have they not answered yet? An unanswered question is not
   *  a refusal to show a client — it is a question still to ask. */
  answered?: boolean;
  /** What the client is here for, so the sentence can name the right rule. Oregon's two answers are
   *  opposite, so the wrong noun here would be a wrong statement of law rather than a typo. */
  service?: string;
  state?: string;
  /** county_no, and under_floor when the county's floor is the one that bit: the jurisdiction as a
   *  person reads it, so a client at the counter is told "Baltimore City", never "MD". */
  county?: string;
  countyQuote?: string;
  countyAuthority?: string | null;
  countyAuthorityUrl?: string | null;
  floorAge?: number;
  placement?: string;
  barred?: string[];
  restricted?: string | null;
  earlobeExempt?: boolean;
  /** TW-0225 — on a piercing's state_no and under_floor, where the state writes some of the ear out of that rule: the
   *  carve-out, so the sentence can tell the client which spot the rule does not reach. */
  earExemption?: EarExemption;
  /** TW-0595 — on a piercing's under_floor where the STATE's floor is the one that bit and its own words except the earlobe:
   *  the exception, so the sentence can tell the client the earlobe is under no floor here. */
  floorEarlobeException?: FloorEarlobeException;
  cite?: string | null;
  citeUrl?: string | null;
};

export type MinorAsk = {
  /** The shop's state, 2 letters, or null when the row has none. */
  state?: string | null;
  /** TW-0153 — the shop's county, or the locality when it is an independent city that has none.
   *  Null for every shop we cannot place, which answers exactly as the state does. */
  county?: string | null;
  /** Which Places component `county` came from. Absent matches a jurisdiction on its NAME alone,
   *  which is the over-refusing direction: it is what a row migrated before this shipped looks
   *  like, and the two Maryland names differ anyway, so nothing is confused by it. */
  countyKind?: string | null;
  /** TW-0227 — the shop's city, Google's locality exactly as Google writes it ("Hammond"), for a city inside a county
   *  whose rule is its own. Null for a row that carries none, which answers as its county or its state. */
  city?: string | null;
  /** The client's age in years, or null before they have said. */
  age?: number | null;
  /** One of service.ts PIERCING_LOCATIONS, or "" before they have picked. */
  placement?: string;
  /** The shop's own setting FOR THIS SERVICE — `public_data.pierce_minors` for a piercing,
   *  `public_data.tattoo_minors` for a tattoo. Off by default, and the caller picks which, because
   *  the exam in the contract carries one boolean per case. */
  shopAllows?: boolean;
  /** What this client is here for today. */
  service?: string;
  /** TW-0153 — TX and TN allow a minor's tattoo ONLY to cover an existing one. null before they have
   *  answered, which is not the same as "no" and must not read as one. */
  coveringExisting?: boolean | null;
  /** TW-0225 — the answer to the ear question (asksEarPiercing), where it was asked: a yes, on a spot the state's carve-out
   *  reaches, takes the state's flat no and its floor off this piercing. null before they have answered, and on any spot
   *  the carve-out does not reach a yes means nothing. */
  earPiercing?: boolean | null;
  /** 2026-10-08 — the shop's stud-gun switch (ruleForShop): omitted, the rule as the contract writes it, which is what
   *  the exam reads; a boolean, a real shop's row, read for true. The form always sends the boolean. */
  studGun?: boolean;
  /** TW-0222 — the product-zone day the question is asked on (day.ts); today's when absent, which is every
   *  live signing. A bar that starts on a day bars nothing before it. */
  today?: string | null;
};

/** THE JURISDICTION THIS SHOP IS IN, out of the state's list, or undefined.
 *
 *  Exact on the name once case and surrounding space are gone, and NOTHING else is normalised —
 *  no suffix stripping, ever. A `.replace(" County", "")` here would make "Baltimore County" match
 *  Baltimore City's flat ban, which is a ban on tattooing minors in the wrong half of Maryland,
 *  shipped as a tidy-up. The kinds are the second lock: a row knows which Places component its
 *  county came from and a rule declares which one it is about, so the two can only ever meet on
 *  purpose. A row with a name and no kind matches on the name — the over-refusing direction, and
 *  what a row migrated before this shipped looks like. */
export function countyRuleFor(
  rule: MinorsRule | undefined,
  county: string | null | undefined,
  countyKind?: string | null,
  city?: string | null
): CountyRule | undefined {
  const want = `${county ?? ""}`.trim().toLowerCase();
  if (!want) return undefined;
  const kind = `${countyKind ?? ""}`.trim().toLowerCase();
  const place = `${city ?? ""}`.trim().toLowerCase();
  return (rule?.counties ?? []).find((c) => {
    // TW-0227 — A CITY INSIDE A COUNTY: the row's city equal to `match` AND its county equal to `within`, the same exact
    // comparison and nothing else, on a row resolved from a county (an independent city belongs to none). A row with no
    // city is never matched by one: it answers as its county or its state. The generator refuses a locality inside a
    // county that has a rule of its own in the same list, so a row can never answer to two.
    if (c.kind === "locality") {
      return (
        Boolean(place) &&
        (!kind || kind === "county") &&
        c.match.trim().toLowerCase() === place &&
        `${c.within ?? ""}`.trim().toLowerCase() === want
      );
    }
    return c.match.trim().toLowerCase() === want && (!kind || kind === c.kind);
  });
}

/** Null when this signing may proceed, else why not — the same eight codes the API answers, checked
 *  in the same order, because the most general refusal is the one a client should be told about.
 *
 *  Called TWICE by the form on purpose. With no placement it answers the AGE-level question, which
 *  is what belongs under the birth date field and what decides whether the guardian block opens at
 *  all; with the placement it answers the whole question, which is what belongs under the placement
 *  picker. Placement is checked last, so one function does both — and for a tattoo the two calls
 *  agree, because there is no placement half. TW-0225's one exception: an ear the state writes out of
 *  its rule, answered yes, is about the SPOT, so the age-level call carries the spot then (Form.tsx),
 *  and a spot a carve-out reaches is never one a state bars. TW-0595's is the second: the Earlobe, where
 *  the floor's own words except it, is carried the same way, and the generator refuses a bar on it too. */
export function minorRefusal(ask: MinorAsk): Refusal | null {
  // TW-0505 — a state whose file has not landed yet is read as no state (`no_state`, which says nothing and keeps the 18+
  // sentence the form has shown since 2023), never as a state whose rule takes no minor (`state_no`, "Tennessee does not
  // allow…"): the rule is on its way, not a no
  const state = ruleState(ask.state);
  const service = `${ask.service ?? ""}`.trim().toLowerCase();
  // the placement half is a PIERCING question. A tattoo rule that allows a minor at all allows it
  // anywhere, so a value left in the picker must not reach the tattoo answer.
  const isPiercing = service === "piercing";
  // 2026-10-08 — as THIS shop meets it: a stud-gun carve-out only while the owner turned the question on (ruleForShop)
  const rule = minorsRuleFor(service, state, ask.studGun === undefined ? undefined : { ask_stud_gun: ask.studGun });
  if (service !== "piercing" && service !== "tattoo") return { code: "no_service" };
  if (!ask.shopAllows) return { code: "shop_off", service };
  if (!state) return { code: "no_state", service };
  if (!rule) return { code: "state_no", service, state };
  // TW-0225 — AN EAR PIERCING THE STATE WRITES OUT OF ITS MINORS RULE, answered yes on a spot the carve-out reaches. The
  // rule does not reach it, so neither does its flat no (Mississippi: "The outer perimeter and the lobe of the ear are
  // outside the definition") nor its floor (Idaho: "nothing in the section, including the age-14 floor, reaches a lobe").
  // Placement bars never name a spot a carve-out reaches (the generator refuses the pair). A tattoo carries none.
  const carveOut = isPiercing ? earExemptionOf(rule) : NO_EAR_EXEMPTION;
  const earOutside = isPiercing && earExemptionApplies(rule, ask.placement, ask.earPiercing);
  const earNote = carveOut.scope !== "none" ? { earExemption: carveOut } : {};
  if (!rule.allowed && !earOutside) {
    return {
      code: "state_no",
      service,
      state,
      cite: rule.cite,
      citeUrl: rule.citeUrl,
      // an earlobe-only state is a real and useful answer, not a flat no. Piercing only: there is
      // no tattoo a state bans generally and permits in one spot.
      earlobeExempt: isPiercing && Boolean(rule.earlobeExempt),
      ...earNote,
    };
  }
  // TW-0153 — THE COUNTY. Maryland allows a minor's tattoo and Baltimore City bans it outright, and
  // until this landed we told a Baltimore shop yes. Read AFTER the state (whose no is the more
  // general answer) and BEFORE the floor (because a jurisdiction that allows nobody has no floor).
  // TW-0227 — read out of the rule for THIS service: each carries its own counties, quoted from its own conditions,
  // so a tattoo's county rule never speaks for a piercing (Vigo County's floor is 16 for one and 14 for the other).
  const county = countyRuleFor(rule, ask.county, ask.countyKind, ask.city);
  if (county?.bans) {
    return {
      code: "county_no",
      service,
      state,
      county: county.label,
      countyQuote: county.quote,
      countyAuthority: county.authority,
      countyAuthorityUrl: county.authorityUrl,
      cite: rule.cite,
      citeUrl: rule.citeUrl,
    };
  }
  // THE HIGHER OF THE TWO FLOORS. Clark County bars body art under 14 where Nevada names none;
  // Philadelphia sets 16 where Pennsylvania names none. Whichever is higher is the one that binds,
  // and the refusal names whichever jurisdiction set it, so a client at the counter is told the
  // rule they can actually go and read.
  // TW-0595 — AND THE STATE'S FLOOR DOES NOT REACH THE SPOT ITS OWN WORDS EXCEPT: Arkansas's "except: ... (B) When piercing
  // the earlobe." and Kentucky's "with the exception of piercing the lobe of the ear." On the Earlobe spot the state's floor
  // is lifted and nothing else is, so a county's own floor (whose rule says nothing of the kind) still binds, and every other
  // demand of the state's rule stands below. Piercing only: a tattoo has no spot.
  const floorEarlobe = isPiercing ? floorEarlobeExceptionOf(rule) : null;
  const floorLifted = isPiercing && floorEarlobeExceptionApplies(rule, ask.placement);
  const stateFloor = !floorLifted && typeof rule.floorAge === "number" ? rule.floorAge : null;
  const countyFloor = typeof county?.floorAge === "number" ? county.floorAge : null;
  const floor = countyFloor !== null && (stateFloor === null || countyFloor > stateFloor) ? countyFloor : stateFloor;
  if (!earOutside && floor !== null && ask.age !== null && ask.age !== undefined && ask.age < floor) {
    const fromCounty = countyFloor !== null && floor === countyFloor && (stateFloor === null || countyFloor > stateFloor);
    return {
      code: "under_floor",
      service,
      state,
      floorAge: Math.trunc(floor),
      ...(fromCounty && county
        ? { county: county.label, countyQuote: county.quote, countyAuthority: county.authority, countyAuthorityUrl: county.authorityUrl }
        : {}),
      cite: rule.cite,
      citeUrl: rule.citeUrl,
      ...earNote,
      // TW-0595 — the state's floor bit and its own words except the earlobe: the sentence says which spot is under none
      ...(floorEarlobe && !fromCounty ? { floorEarlobeException: floorEarlobe } : {}),
    };
  }
  // TW-0153 — the one requirement the FORM can settle, because it is a fact about today's tattoo
  // rather than something the shop does once. AFTER the floor, which is the more general refusal:
  // Tennessee's floor is 16, so a 15-year-old there is refused whatever this tattoo covers, and
  // saying "only to cover an existing tattoo" would send them off to read a rule that does not
  // decide their case. This sat the other way round until 2026-09-10, agreeing with the comment it
  // no longer had — the API had it right and the exam pinned every TN floor case with the cover-up
  // answered YES, so no case could tell the two apart.
  // TW-0222 / TW-0226 — the day the question is asked on, for a bar that starts on a day and a requirement that does
  const today = `${ask.today ?? ""}`.trim() || dayOf();
  if (requiresCoverupOnly(rule, today) && ask.coveringExisting !== true) {
    return {
      code: "coverup_only",
      service,
      state,
      answered: ask.coveringExisting === false,
      cite: rule.cite,
      citeUrl: rule.citeUrl,
    };
  }
  // TW-0222 — a bar that starts on a day (North Dakota's genital and nipple bar, 2026-10-01) bars nothing before it
  const barStarts = `${rule.restrictedFrom ?? ""}`.trim();
  const barred = isPiercing && (!barStarts || barStarts <= today) ? rule.restrictedPlacements ?? [] : [];
  // TW-0227 — AND THE COUNTY'S OWN BARS (Vigo County: "No person shall pierce the genitalia or nipples of a person under
  // the age of 18.", § 3-56.29(B)) on top of the state's. The refusal names the county only when its bar is the one
  // that bit, so a client is sent to read the rule that decides their case.
  const countyBarred = isPiercing ? (county?.restrictedPlacements ?? []).filter((p) => !barred.includes(p)) : [];
  const every = [...barred, ...countyBarred];
  const countyFacts = county
    ? { county: county.label, countyQuote: county.quote, countyAuthority: county.authority, countyAuthorityUrl: county.authorityUrl }
    : {};
  const picked = isPiercing ? `${ask.placement ?? ""}`.trim() : "";
  if (every.length && picked === UNVERIFIABLE_PLACEMENT) {
    return {
      code: "placement_unverifiable",
      state,
      barred: every,
      ...(barred.length ? {} : countyFacts),
      cite: rule.cite,
      citeUrl: rule.citeUrl,
    };
  }
  if (picked && every.includes(picked)) {
    return {
      code: "placement_no",
      state,
      placement: picked,
      restricted: rule.restricted,
      ...(barred.includes(picked) ? {} : countyFacts),
      cite: rule.cite,
      citeUrl: rule.citeUrl,
    };
  }
  return null;
}

/** Does this state allow a minor's tattoo ONLY as a cover-up? TX and TN, from the contract. */
export function requiresCoverupOnly(rule: MinorsRule | undefined, day: string = dayOf()): boolean {
  return requiresOn(rule, day).some((r) => r.id === "coverup_only");
}

/** TW-0153 — does this jurisdiction want the MINOR's OWN photo ID as well as the parent's?
 *
 *  Read from the state's list AND its county's, because El Paso County CO asks for it where
 *  Colorado does not. Florida and Nevada carry it statewide — Nevada's own guide for establishments
 *  lists a "Copy of identification, with photo" and a "Copy of legal guardian, if required" as two
 *  separate lines on a client release form, which is what Jose Velazquez of A Toda Madre Tattoos in
 *  Reno wrote in and asked for on his first morning as a customer, 2026-09-10. He was right about
 *  his state; the source was read before this shipped, not his email.
 *
 *  Until now the form took ONE photo and, for a minor, made it the parent's — which is the right
 *  single answer and the wrong one where a state wants two. */
export function requiresBothIds(
  rule: MinorsRule | undefined,
  county: string | null | undefined,
  countyKind?: string | null,
  city: string | null = null,
  day: string = dayOf()
): boolean {
  // TW-0226 — on the day asked: North Dakota's is in force from 2026-10-01. A county's entries carry no window.
  const has = (list?: Requirement[]) => (list ?? []).some((r) => r.id === "both_ids" && requirementInForce(r, day));
  return has(rule?.requires) || has(countyRuleFor(rule, county, countyKind, city)?.requires);
}

/** 2026-09-24 — does this jurisdiction want PROOF THAT THE ADULT IS THE PARENT OR GUARDIAN — the minor's
 *  birth certificate, or the adoption, custody or guardianship order — on top of the adult's own photo ID?
 *
 *  Read from the state's list AND its county's, like `requiresBothIds`. Arkansas, Florida, Michigan,
 *  Nebraska, Nevada, Texas and Utah carry it statewide, Philadelphia on its own (El Paso County's letter of
 *  guardianship is wanted only when a guardian signs, so it is not the proof every adult is asked for, TW-0226); Michigan,
 *  Nebraska and Nevada say in so many words that the shop KEEPS A COPY, and until this the signed waiver
 *  could not hold one: the shop ticked "We check that this adult really is the parent or guardian" in
 *  Settings and the file showed nothing. Since TW-0224 (2026-09-25) the PIERCING rule carries its own
 *  `requires`, read from its own conditions (AR, GA, MI, NE, NV, NH, OK and UT name the proof for a piercing),
 *  so this answers for whichever rule it is handed; where neither names it, the shop's own switch
 *  (`asksGuardianProof`) is what turns it on. Since TW-0227 a piercing rule carries its own counties too (Vigo
 *  County's proof of guardianship), read from the piercing rule handed in, never the tattoo's. */
export function requiresGuardianProof(
  rule: MinorsRule | undefined,
  county: string | null | undefined,
  countyKind?: string | null,
  city: string | null = null,
  day: string = dayOf()
): boolean {
  const has = (list?: Requirement[]) => (list ?? []).some((r) => r.id === "guardian_proof" && requirementInForce(r, day));
  return has(rule?.requires) || has(countyRuleFor(rule, county, countyKind, city)?.requires);
}

/** TW-0227 — does this jurisdiction want BOTH parents' written permission, or the legal guardian's, where the state
 *  asks one parent? Hammond and East Chicago (`both_parents`: "written permission from both parents or guardians, if
 *  not deceased") and Delaware County (`both_parents_present`), from the state's list AND its county's or city's, for
 *  whichever rule it is handed: all three ordinances name a piercing in the same sentence as a tattoo. The API reads the
 *  same (state_forms.requires_both_parents). */
export function requiresBothParents(
  rule: MinorsRule | undefined,
  county: string | null | undefined,
  countyKind?: string | null,
  city: string | null = null,
  day: string = dayOf()
): boolean {
  const has = (list?: Requirement[]) =>
    (list ?? []).some((r) => (r.id === "both_parents" || r.id === "both_parents_present") && requirementInForce(r, day));
  return has(rule?.requires) || has(countyRuleFor(rule, county, countyKind, city)?.requires);
}

/** …and must the second parent be HERE (TW-0227)? Delaware County: "Both parents or minor’s legal guardian must be
 *  present and provide consent in writing." Where this is the rule the form offers the other parent's signature at
 *  the counter, or the word that there is no second parent, and never a photo of a permission signed elsewhere. */
export function requiresBothParentsPresent(
  rule: MinorsRule | undefined,
  county: string | null | undefined,
  countyKind?: string | null,
  city: string | null = null,
  day: string = dayOf()
): boolean {
  const has = (list?: Requirement[]) => (list ?? []).some((r) => r.id === "both_parents_present" && requirementInForce(r, day));
  return has(rule?.requires) || has(countyRuleFor(rule, county, countyKind, city)?.requires);
}

/** TW-0227 — WHOSE rule wants both parents, for the sentence that says so on the form: the county's or city's own label
 *  ("Hammond", "East Chicago", "Delaware County") when its own list carries `both_parents` or `both_parents_present` in
 *  force on `day`, else null, which is the state's own list and is named by the state (no state carries either today).
 *  The API names it the same way (state_forms.both_parents_place). */
export function bothParentsPlace(
  rule: MinorsRule | undefined,
  county: string | null | undefined,
  countyKind?: string | null,
  city: string | null = null,
  day: string = dayOf()
): string | null {
  const local = countyRuleFor(rule, county, countyKind, city);
  const carries = (local?.requires ?? []).some(
    (r) => (r.id === "both_parents" || r.id === "both_parents_present") && requirementInForce(r, day)
  );
  return local && carries ? local.label : null;
}

/** TW-0225 — WHAT THE RULE ITSELF ASKS OF THE CLIENT AT SIGNING, of the two a client answers with a photo: `both_ids` (the
 *  minor's own ID) and `guardian_proof` (the birth certificate or guardianship papers), in that order. None of them when
 *  the ear carve-out applies to this signing (a yes on a spot it reaches): the state's rule does not reach the piercing,
 *  so neither does its list. The shop's own proof switch is not the rule and is read beside this (asksGuardianProof).
 *  The same answer as the API's `state_forms.rule_asks_for`; the exam in the contract holds the two together. The county's
 *  half goes with the state's under the carve-out: Vigo County, the one piercing county naming a photo, leaves the earlobe
 *  out the way Indiana does (§ 3-56.01), and the both-parents rules are not photos and are not read here. */
export function ruleAsksFor(
  rule: MinorsRule | undefined,
  ask: {
    placement?: string | null;
    earPiercing?: boolean | null;
    county?: string | null;
    countyKind?: string | null;
    /** TW-0227 — the shop's city (the Places locality), for a city inside a county whose rule is its own (Hammond). */
    city?: string | null;
    day?: string | null;
  } = {}
): ("both_ids" | "guardian_proof")[] {
  if (earExemptionApplies(rule, ask.placement, ask.earPiercing)) return [];
  const day = `${ask.day ?? ""}`.trim() || dayOf();
  // TW-0227 — the readers take (rule, county, countyKind, city, day): the city sits where the day used to, and both are
  // strings, so a day handed over in the old slot type-checks, is read as a city, and asks the requirement on today
  const city = ask.city ?? null;
  const out: ("both_ids" | "guardian_proof")[] = [];
  if (requiresBothIds(rule, ask.county, ask.countyKind, city, day)) out.push("both_ids");
  if (requiresGuardianProof(rule, ask.county, ask.countyKind, city, day)) out.push("guardian_proof");
  return out;
}

/** TW-0270 slice 5 — the papers this rule, and the shop's own county's or city's rule inside it, keeps with a minor's record on
 *  `route`, in force on `day`: the state's in its order, then the jurisdiction's the state does not name already, each of those
 *  carrying `place`. The API's state_forms.papers_for is the same read. */
export function papersFor(
  rule: MinorsRule | undefined,
  ask: { county?: string | null; countyKind?: string | null; city?: string | null; day?: string | null; route: Paper["route"] }
): Paper[] {
  const day = `${ask.day ?? ""}`.trim() || dayOf();
  const out: Paper[] = (rule?.papers ?? [])
    .filter((paper) => (paper.route ?? "guardian") === ask.route && requirementInForce(paper, day))
    .map((paper) => {
      const kept: Paper = { ...paper };
      delete kept.from;
      delete kept.until;
      return kept;
    });
  const seen = new Set(out.map((paper) => paper.id));
  const local = countyRuleFor(rule, ask.county, ask.countyKind, ask.city ?? null);
  for (const paper of local?.papers ?? []) {
    if (seen.has(paper.id) || (paper.route ?? "guardian") !== ask.route) continue;
    seen.add(paper.id);
    out.push({ ...paper, place: local!.label });
  }
  return out;
}

/** TW-0270 slice 5 — what the rule asks a photo of at signing on `route`: papersFor, and none where the ear carve-out applies to
 *  this signing (earExemptionApplies), the way ruleAsksFor empties the minor's ID and the birth certificate: the state's rule does
 *  not reach that piercing, so neither do its papers. The API's state_forms.papers_asked is the same read.
 *
 *  `ticked` (2026-11-03) is the boxes the SHOP has ticked for this rule's service (minorsTicked): a paper that waits on its box
 *  (`onceTicked`, New York's and Wisconsin's state form) is asked only when that box is a literal true there (Chris, that morning:
 *  "we shouldn't be asking customers to dig around in settings"), so a shop whose switch was on before the box existed is never
 *  stopped at the counter for a form it may not have. Undefined is the rule as the contract writes it, which no signing is judged
 *  by; null is a shop that ticked nothing. A paper that waits on no box (Arkansas's) is asked whatever the boxes say. */
export function papersAsked(
  rule: MinorsRule | undefined,
  ask: {
    placement?: string | null;
    earPiercing?: boolean | null;
    county?: string | null;
    countyKind?: string | null;
    city?: string | null;
    day?: string | null;
    route: Paper["route"];
    ticked?: TickedBoxes | null;
  }
): Paper[] {
  if (earExemptionApplies(rule, ask.placement, ask.earPiercing)) return [];
  const papers = papersFor(rule, ask);
  if (ask.ticked === undefined) return papers;
  return papers.filter((paper) => !paper.onceTicked || ask.ticked?.[paper.onceTicked] === true);
}

/** 2026-11-03 — every box the state's rule for `service` asks a shop to tick, ticked: the DEMO shop's answers while its minors
 *  dial is on (App.tsx withDemo), because a real shop's switch goes on only once every box is ticked (TW-0153), so a paper that
 *  waits on its box (New York's and Wisconsin's state form) is asked on the demo as it is at a new shop's counter. */
export function everyBoxTicked(service: string, state: string | null | undefined): TickedBoxes {
  const out: TickedBoxes = {};
  for (const requirement of minorsRuleFor(service, state)?.requires ?? []) out[requirement.id] = true;
  return out;
}

/** 2026-11-03 — the boxes this shop ticked under the switch for `service` (`public_data.pierce_minors_ack` for a piercing,
 *  `tattoo_minors_ack` for a tattoo), or null for a shop that ticked none and for a service with no minors switch. The API reads
 *  the same answers (common.minors_ack_of). */
export function minorsTicked(
  service: string | null | undefined,
  publicData: { pierce_minors_ack?: TickedBoxes; tattoo_minors_ack?: TickedBoxes } | null | undefined
): TickedBoxes | null {
  const s = `${service ?? ""}`.trim().toLowerCase();
  if (s === "piercing") return publicData?.pierce_minors_ack ?? null;
  if (s === "tattoo") return publicData?.tattoo_minors_ack ?? null;
  return null;
}

/** Does THIS shop's form ask for a photo of the birth certificate or guardianship papers when a parent or
 *  guardian signs? The shop's own switch (Settings → Who can sign → Birth certificate or guardianship
 *  papers: `public_data.ask_guardian_proof_photo`, a literal true), OR the rule for the service being signed
 *  naming it — the same "the rule wins, the switch is the owner's elsewhere" shape as the artist's signature.
 *  The API reads the same two things the same way (app.py `wants_guardian_proof`). Kelly Voris of The Tattoo
 *  Lady (Hammond IN) asked on the ad on 2026-09-24: "Is there a way to attach id's and or birth certificate
 *  to it?" — Indiana names no proof, so for her shop it is the switch. */
export function asksGuardianProof(
  publicData: { ask_guardian_proof_photo?: boolean } | undefined,
  rule: MinorsRule | undefined,
  county: string | null | undefined,
  countyKind?: string | null,
  city: string | null = null,
  day: string = dayOf(),
  // TW-0225 — an ear piercing the rule does not reach (earExemptionApplies): the rule's half is not asked, and the
  // owner's own switch still is, because it is the owner's rule and not the state's
  earOutside: boolean = false
): boolean {
  return (
    publicData?.ask_guardian_proof_photo === true || (!earOutside && requiresGuardianProof(rule, county, countyKind, city, day))
  );
}

/** TW-0267 — THE STATEMENT THIS RULE HAS THE PARENT OR GUARDIAN TICK, in force on `day`, or null: Tennessee's
 *  certification for a minor's piercing, and nothing for a tattoo there (its tattoo rule is another chapter, and allows a
 *  minor only to cover an existing tattoo). Read from the rule for the service being signed, so the caller asks it only
 *  on a guardian route. NOT emptied by the ear carve-out: a yes there takes the rule's photos, its floor and its flat no
 *  off a signing, never the parent's own consent (earExemptionApplies), and this statement is that consent in the state's
 *  words. The API reads the same list the same way (state_forms.guardian_attestation_for). */
export function guardianAttestationFor(rule: MinorsRule | undefined, day: string = dayOf(), placement: string = ""): Attestation | null {
  return guardianAttestationsFor(rule, placement, day)[0] ?? null;
}

/** TW-0526, the client-form side (2026-10-29) — EVERY STATEMENT THIS RULE HAS THE PARENT OR GUARDIAN TICK on this signing, in the
 *  rule's order: Illinois's piercing rule three since that night (its 797.400(k) disclosure statement, the parent's word that they
 *  sign in front of the operator or body artist, and (l)'s provision for a piercing in the mouth). In force on `day`, and a
 *  statement kept for some placements (`placements`: the picker's five in or through the mouth) only when `placement`, the
 *  piercing's posted spot, is one of them. The first is the one guardianAttestationFor has always answered, posted by the pair it
 *  always was (`guardianCertification` and its id); each after it is a tick of its own in `guardianStatements` (Form.tsx). The API
 *  reads the same list the same way (state_forms.guardian_attestations_for). */
export function guardianAttestationsFor(rule: MinorsRule | undefined, placement: string = "", day: string = dayOf()): Attestation[] {
  const spot = `${placement ?? ""}`.trim();
  return (rule?.attestations ?? []).filter(
    (a) =>
      a.who === "guardian" &&
      Boolean(a.text) &&
      requirementInForce(a, day) &&
      (!a.placements?.length || a.placements.includes(spot)),
  );
}

/** TW-0153 — does this state treat an emancipated minor as an ADULT? Yes where its rule carries
 *  `emancipated_adult`, each state saying so in its own words, and the contract is the list: on
 *  2026-10-18 the tattoo rules of CT, DE, ID, MI, NV and UT and the piercing rules of CT, DE, FL, ID and
 *  UT (Florida's piercing since 2026-11-04, Fla. Admin. Code R. 64E-19.002(14), TW-0627), which
 *  minors.test.ts reads off the contract and holds this comment to (TW-0336). The only
 *  requirement that opens a door instead of closing one: an emancipated 17-year-old cannot complete
 *  the guardian route, because there is no parent to sign it.
 *
 *  Deliberately NOT read inside `minorRefusal`. That function answers whether this signing may
 *  proceed, and on every rule that carries it the claim cannot change that answer: each is `allowed`
 *  (the same test holds it), so past any floor age (Idaho's 14) a minor may proceed either way. What
 *  a claim changes is whether the form asks for a parent, which is the caller's question and is
 *  answered by `signsAsAdult` below. Keeping them apart is what stops the claim from ever reading as
 *  a way past a floor age or a flat ban — those refuse everyone, not every minor. */
export function allowsEmancipatedAdult(rule: MinorsRule | undefined, day: string = dayOf()): boolean {
  return requiresOn(rule, day).some((r) => r.id === "emancipated_adult");
}

/** Does this client sign for themselves? True only when they are a minor, this state hands an
 *  emancipated minor the adult route, the signing is otherwise ALLOWED, and they said yes.
 *
 *  `refusal` is the age-level answer. Requiring it to be null is the load-bearing part: a state
 *  that refuses a minor outright, a county that bans it, and a floor age all keep refusing, so the
 *  question can never become the escape hatch from a rule that was never about parents. */
export function signsAsAdult(
  rule: MinorsRule | undefined,
  refusal: Refusal | null,
  claimed: boolean | null | undefined,
  day: string = dayOf()
): boolean {
  return claimed === true && refusal === null && allowsEmancipatedAdult(rule, day);
}

/** THE THREE REFUSALS THE FORM DOES NOT SPEAK FOR. No service picked yet, a shop that has not
 *  turned this on for what the client is here for, and a shop whose state we do not know all keep
 *  the 18+ refusal this form has shown since 2023, word for word — there is nothing useful to say
 *  about a rule we are not applying, and a sentence there would advertise the setting to every shop
 *  that never asked for it. The other four name the state's own rule. */
const SILENT: RefusalCode[] = ["no_service", "shop_off", "no_state"];

export const speaksForItself = (refusal: Refusal | null): boolean => {
  if (!refusal || SILENT.includes(refusal.code)) return false;
  // a cover-up question nobody has answered yet is a question, not a refusal: saying "this state
  // only allows a cover-up" before they have been asked reads as a wall they cannot get past
  if (refusal.code === "coverup_only") return refusal.answered === true;
  return true;
};


/** The state's own name, for a sentence a client reads: "Oregon does not allow…", never "OR". */
export const stateNameOf = (abbr: string | undefined): string =>
  (abbr && STATE_NAMES[abbr]) || `${abbr ?? ""}`;

/** WHAT THE PARENT IS CONSENTING TO, in the state's own words — quoted on the screen and printed on
 *  the signed waiver, never summarised: every state's list is different and the shop is the one who
 *  has to satisfy it. "" when this state hands the parent no route, which is when nothing should be
 *  quoted at all. */
export function guardianConditionsFor(
  service: string | null | undefined,
  state: string | null | undefined
): string {
  const rule = minorsRuleFor(service, state);
  return rule?.allowed ? rule.conditions ?? "" : "";
}

/** 2026-11-03 — A STATE THAT SETS NO RULE FOR THIS SERVICE (`no_state_rule`: Hawaii, Iowa, Vermont and Washington, for a
 *  piercing). The words guardianConditionsFor hands back there are the shop's own house rule (the record's `formConditions`),
 *  never the state's, so the heading over them is the shop's (i18n guardianShopWants) and the demo says the shop's rule prints.
 *  The signed waiver's heading is the API's same branch on the same field (state_forms.guardian_conditions_heading). */
export function stateSetsNoRule(service: string | null | undefined, state: string | null | undefined): boolean {
  return minorsRuleFor(service, state)?.parentalConsent === "no_state_rule";
}

/** A placement in the client's own language, so a Spanish refusal names a Spanish site. Falls back
 *  to the value, which is what the picker posts and what the record carries. */
export function placementLabel(value: string | undefined, lang: Lang): string {
  const option = PIERCING_LOCATIONS.find((l) => l.value === value);
  return option ? locationLabel(option, lang) : `${value ?? ""}`;
}

/** WHAT THE CLIENT READS, or "" when this refusal is one the form does not speak for.
 *
 *  Every sentence names the state and, where there is one, the rule behind it — a shop told "no"
 *  has to be able to tell our bug from their state's law, and the client standing at the counter is
 *  the one who shows them. */
export function minorSentence(refusal: Refusal | null, T: Strings, lang: Lang): string {
  if (!speaksForItself(refusal)) return "";
  const r = refusal as Refusal;
  const state = stateNameOf(r.state);
  // TW-0153 — WHICHEVER JURISDICTION SET THE RULE IS THE ONE NAMED. A client in Baltimore City is
  // told "Baltimore City", not "Maryland", because Maryland allows what their city bars and a
  // sentence blaming the state would send them to read the wrong law. `county` is on the refusal
  // only when the county is what bit.
  const who = r.county || state;
  const parts: string[] = [];
  if (r.code === "state_no" || r.code === "county_no") {
    parts.push(T.minorStateNo(who, `${r.service ?? ""}`));
    // TW-0225 — where the state writes the ear out of this rule, the form can act on it: the sentence says which spot to
    // pick, and the question under that spot opens the route. Anywhere else an ear carve-out the form cannot act on (DC's
    // ear is the rule's one permission, not an exemption) is still a real answer, and at a counter it turns a dead end
    // into a question for the piercer standing right there.
    if (r.earExemption) parts.push(T.minorEarOutsideNote(r.earExemption.scope, `${r.earExemption.method ?? ""}`));
    else if (r.earlobeExempt) parts.push(T.minorEarlobeNote);
  } else if (r.code === "under_floor") {
    parts.push(T.minorUnderFloor(who, `${r.floorAge}`, `${r.service ?? ""}`));
    // TW-0595 — Arkansas's floor and Kentucky's name the earlobe as their own exception, with no tool: the earlobe is the
    // spot to pick, whatever pierces it, and the parent's part still applies. Ahead of the carve-out's sentence, which on
    // Arkansas's stud-gun shops would say the same spot for one tool only
    if (r.floorEarlobeException) parts.push(T.minorFloorEarlobeNote);
    // TW-0225 — Idaho's floor, Wisconsin's and Arkansas's do not reach the ear their rule writes out
    else if (r.earExemption) parts.push(T.minorEarOutsideNote(r.earExemption.scope, `${r.earExemption.method ?? ""}`));
  } else if (r.code === "coverup_only") {
    parts.push(T.minorCoverupOnly(state));
  } else if (r.code === "placement_no") {
    // TW-0227 — `who`, because a county can bar a site its state does not (Vigo County's genital and nipple)
    parts.push(T.minorPlacementNo(who, placementLabel(r.placement, lang)));
  } else if (r.code === "placement_unverifiable") {
    parts.push(T.minorPlacementUnverifiable(who));
  }
  // a local rule stands in its own name: the cite on the refusal is the STATE's, which is the rule
  // this jurisdiction is stricter THAN, so printing it under a county's no would name the wrong law
  if (r.county) parts.push(T.minorCountyRule(state));
  else if (r.cite) parts.push(T.minorCite(r.cite));
  return parts.join(" ");
}
